import { normalize } from './index.js';
import { fuzzyMatches, editDistance, typoAllowance } from './search.js';

// People look for their country by the name they use for it, not the name on the ISO register: UK,
// USA, Holland, Deutschland, England. Codes are matched exactly so "UK" cannot land on Ukraine.
const COLLOQUIAL: Record<string, string[]> = {
  GB: [
    'uk',
    'u k',
    'great britain',
    'britain',
    'england',
    'scotland',
    'wales',
    'northern ireland',
    'british',
  ],
  US: ['usa', 'u s a', 'us', 'america', 'united states', 'the states', 'american'],
  AE: ['uae', 'u a e', 'emirates', 'dubai', 'abu dhabi'],
  NL: ['holland', 'nederland', 'dutch'],
  DE: ['deutschland', 'german'],
  ES: ['espana', 'espanya', 'spanish'],
  IT: ['italia', 'italian'],
  FR: ['french'],
  IE: ['eire', 'republic of ireland', 'irish'],
  SE: ['sverige', 'swedish'],
  DK: ['danmark', 'danish'],
  FI: ['suomi', 'finnish'],
  NO: ['norge', 'norsk', 'norwegian'],
  AT: ['osterreich', 'austrian'],
  CH: ['schweiz', 'suisse', 'svizzera', 'swiss'],
  BE: ['belgie', 'belgique', 'belgian'],
  PL: ['polska', 'polish'],
  CZ: ['czechia', 'ceska republika', 'czech'],
  GR: ['hellas', 'ellada', 'greek'],
  PT: ['portuguese'],
  HU: ['magyarorszag', 'hungarian'],
  RO: ['romania', 'romanian'],
  HR: ['hrvatska', 'croatian'],
  SI: ['slovenija', 'slovenian'],
  SK: ['slovensko', 'slovakian'],
  LU: ['luxemburg'],
  CY: ['kypros', 'cypriot'],
  MT: ['maltese'],
  BG: ['balgariya', 'bulgarian'],
  EE: ['eesti', 'estonian'],
  LV: ['latvija', 'latvian'],
  LT: ['lietuva', 'lithuanian'],
  CA: ['canadian'],
  AU: ['aussie', 'australian'],
  NZ: ['aotearoa', 'new zealand'],
  KR: ['south korea', 'korea', 'republic of korea'],
  RU: ['russia', 'russian federation'],
  TR: ['turkiye', 'turkey'],
  IN: ['bharat', 'indian'],
  CN: ['china', 'prc', 'mainland china'],
  JP: ['nippon', 'nihon', 'japanese'],
  BR: ['brasil', 'brazilian'],
  MX: ['mexico', 'mejico'],
  ZA: ['rsa', 'south africa'],
  SA: ['ksa', 'saudi', 'saudi arabia'],
};

export type SearchableCountry = { code: string; name: string; alias?: string; alpha3?: string };

/** Every string that should find this country, most authoritative first. */
export function countryTerms(country: SearchableCountry) {
  return [
    normalize(country.name),
    normalize(country.alias ?? ''),
    ...(COLLOQUIAL[country.code] ?? []),
  ].filter(Boolean);
}

/**
 * Rank a country against what was typed. Codes match exactly and outrank everything, so "UK" reaches
 * the United Kingdom rather than Ukraine, which merely contains those letters. Lower is better.
 */
export function countryRank(country: SearchableCountry, query: string) {
  const term = normalize(query);
  if (!term) return 0;
  const codes = [country.code.toLowerCase(), (country.alpha3 ?? '').toLowerCase()].filter(Boolean);
  if (codes.includes(term)) return 0;
  const terms = countryTerms(country);
  if (terms.some((value) => value === term)) return 1;
  if (terms.some((value) => value.startsWith(term))) return 2;
  if (terms.some((value) => value.split(' ').some((word) => word.startsWith(term)))) return 3;
  if (terms.some((value) => value.includes(term))) return 4;
  const limit = typoAllowance(term);
  if (
    limit &&
    terms.some((value) => value.split(' ').some((word) => editDistance(word, term, limit) <= limit))
  )
    return 5;
  if (limit && terms.some((value) => fuzzyMatches(value, term))) return 6;
  return Number.POSITIVE_INFINITY;
}

/** The countries worth showing for a query, best match first, original order kept within a rank. */
export function searchCountries<T extends SearchableCountry>(countries: T[], query: string) {
  const term = normalize(query);
  if (!term) return countries;
  return countries
    .map((country, index) => ({ country, index, rank: countryRank(country, term) }))
    .filter((entry) => entry.rank !== Number.POSITIVE_INFINITY)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.country);
}
